/* © NXAPP Limited */
/**
 * Tiny helpers for the module dashboards: bucket event rows into per-day counts
 * (London time), so a card can show Today / This week / This month + a sparkline
 * from one fetch. Buckets are oldest→newest; the last element is "today".
 */

/** YYYY-MM-DD for a date in Europe/London (DST-aware via Intl). */
export function londonDayKey(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d)
}

/** The last `days` London day-keys, oldest first, ending today. */
export function dayKeys(days: number): string[] {
  const keys: string[] = []
  const now = Date.now()
  for (let i = days - 1; i >= 0; i--) keys.push(londonDayKey(new Date(now - i * 86_400_000)))
  return keys
}

/** Count rows into per-day buckets by a timestamp field. */
export function bucketCount<T>(rows: T[], keys: string[], ts: (r: T) => string | null | undefined): number[] {
  const idx = new Map(keys.map((k, i) => [k, i]))
  const out = new Array(keys.length).fill(0)
  for (const r of rows) {
    const v = ts(r); if (!v) continue
    const i = idx.get(londonDayKey(new Date(v)))
    if (i !== undefined) out[i]++
  }
  return out
}

/** Sum a numeric field into per-day buckets by a timestamp field. */
export function bucketSum<T>(rows: T[], keys: string[], value: (r: T) => number, ts: (r: T) => string | null | undefined): number[] {
  const idx = new Map(keys.map((k, i) => [k, i]))
  const out = new Array(keys.length).fill(0)
  for (const r of rows) {
    const v = ts(r); if (!v) continue
    const i = idx.get(londonDayKey(new Date(v)))
    if (i !== undefined) out[i] += value(r) || 0
  }
  return out
}

/** Element-wise sum of two equal-length series. */
export function addSeries(a: number[], b: number[]): number[] {
  return a.map((v, i) => v + (b[i] ?? 0))
}

export const sumSeries = (s: number[]): number => s.reduce((a, b) => a + b, 0)

/** A metric split into the three periods shown on every row. */
export interface Split { today: number; week: number; month: number }
export const emptySplit = (): Split => ({ today: 0, week: 0, month: 0 })

/**
 * Add one event's value into a split: always into month (rows are pre-filtered to
 * ~30 days), into week if within the last 7 days, into today if it's today in
 * London. Pass a shared nowMs/todayKey when looping for speed.
 */
export function addToSplit(
  s: Split, ts: string | null | undefined, val: number,
  nowMs = Date.now(), todayKey = londonDayKey(new Date()),
): void {
  if (!ts) return
  const t = new Date(ts).getTime()
  s.month += val
  if (t >= nowMs - 7 * 86_400_000) s.week += val
  if (londonDayKey(new Date(ts)) === todayKey) s.today += val
}
